/**
 * /dashboard/payroll — Payroll runs list (G5.5).
 *
 * HR-admin landing page. The PayrollRoleGate inside PayrollModuleShell
 * is the in-page UX mirror of the backend's RBAC on every /payroll/*
 * route (RequireRole company_admin / hr_manager) — backend stays the
 * boundary; gate is presentation.
 *
 * Migration note (Phase 3e — 2026-06-01): port of
 * frontend/src/app/(dashboard)/dashboard/payroll/page.tsx with:
 *   - "use client" dropped
 *   - next/link → @tanstack/react-router Link
 *   - PayrollRoleGate + PageHeader hoisted into PayrollModuleShell so
 *     the three Payroll routes share one frame.
 */

import type { ReactNode } from "react";
import { REGISTERS_AND_RETURNS } from "@/lib/payroll-pages";
import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { ArrowRight, BadgeIndianRupee, FileText, Plus } from "lucide-react";
import { PayrollModuleShell } from "@/components/shared/payroll-module-shell";
import { RunList } from "@/components/payroll/run-list";
import {
  FirstPayrollDoneBanner,
  FirstPayrollGuide,
  FirstPayrollGuideSkeleton,
  PayrollSetupUnavailable,
} from "@/components/payroll/payroll-setup-checklist";
import { nextPayrollMonth, openDraftFor, payrollPageMode } from "@/lib/payroll-setup";
import { todayIndiaISO } from "@/lib/payroll-month";
import { MONTH_LABELS } from "@/types/payroll";
import { Button } from "@/components/ui/button";
import { payrollKeys, usePayrollRuns, usePayrollSetup } from "@/hooks/use-payroll";
import { directoryKeys } from "@/hooks/use-directory";
import { directoryApi, payrollApi } from "@/lib/api";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/payroll/")({
  // A9 hover-prefetch. Primary = the current-year runs list (RunList's
  // initial year state is the current year — deterministic). The nav link
  // is HR-admin-only, so in practice the loader fires for callers the
  // backend RBAC accepts; a deep-linking non-admin just gets a caught 403
  // and the in-page role gate renders as today. The include-inactive
  // directory (run-creator names) is secondary → fire-and-forget.
  loader: ({ context }) => {
    void context.queryClient.prefetchQuery({
      queryKey: directoryKeys.list(true),
      queryFn: ({ signal }) =>
        directoryApi.listDirectory({ includeInactive: true }, signal),
    });
    const year = new Date().getFullYear();
    return context.queryClient
      .ensureQueryData({
        queryKey: payrollKeys.runs(year),
        queryFn: ({ signal }) => payrollApi.listRuns({ year }, signal),
      })
      .catch(() => undefined);
  },
  component: PayrollListRoute,
});

function PayrollListRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  // "Your first payroll" (switching-and-first-payroll T8): until the first
  // run is locked the guide leads and owns the one primary action — "New
  // run" is hidden (step 5 creates the draft). If the setup read fails,
  // "New run" stays the primary with a quiet note (today's fallback, said
  // out loud).
  const setup = usePayrollSetup();
  const mode = payrollPageMode(setup);
  // "Start <month> payroll" opens the next month not yet locked — never a
  // month the guide just locked (that create is refused).
  const { year, month } = nextPayrollMonth(setup.data, todayIndiaISO());
  // That month's draft may already be open: then the one primary opens it.
  const runs = usePayrollRuns(year);
  const draftId = openDraftFor(runs.data?.items, year, month);

  const registersLink = (
    <Link to="/app/$tenantSlug/payroll/statutory" params={{ tenantSlug }}>
      <FileText aria-hidden className="h-4 w-4" />
      {REGISTERS_AND_RETURNS}
    </Link>
  );

  // While the guide shows the header carries nothing: step 5 owns "create",
  // and Registers & returns stays one tap away in the payroll tabs above.
  let actions: ReactNode = null;
  if (mode === "runs" || mode === "error") {
    actions = (
      <div className="flex flex-wrap gap-2">
        <Button asChild variant="outline" className="gap-1.5">
          {registersLink}
        </Button>
        <Button asChild className="gap-1.5" data-testid="payroll-list-new-button">
          {mode === "runs" && draftId != null ? (
            <Link to="/app/$tenantSlug/payroll/$id" params={{ tenantSlug, id: String(draftId) }}>
              Open {MONTH_LABELS[month - 1]} draft
              <ArrowRight aria-hidden className="h-4 w-4" />
            </Link>
          ) : mode === "runs" ? (
            <Link to="/app/$tenantSlug/payroll/new" params={{ tenantSlug }} search={{ year, month }}>
              <Plus aria-hidden className="h-4 w-4" />
              Start {MONTH_LABELS[month - 1]} payroll
            </Link>
          ) : (
            <Link to="/app/$tenantSlug/payroll/new" params={{ tenantSlug }}>
              <Plus aria-hidden className="h-4 w-4" />
              New run
            </Link>
          )}
        </Button>
      </div>
    );
  }

  return (
    <PayrollModuleShell
      title="Runs"
      description="Run monthly payroll: salaries, payslips, statutory filings and bank-transfer files, with a CSV export of every run."
      actions={actions}
    >
      <div className="flex items-center gap-2 text-sm text-muted-foreground sm:hidden">
        <BadgeIndianRupee aria-hidden className="h-4 w-4" />
        <span>HR-admin view</span>
      </div>
      {mode === "loading" && <FirstPayrollGuideSkeleton />}
      {mode === "error" && (
        <div className="mb-4">
          <PayrollSetupUnavailable onRetry={() => void setup.refetch()} />
        </div>
      )}
      {mode === "guide" && setup.data && (
        <div className="mb-6">
          <FirstPayrollGuide tenantSlug={tenantSlug} setup={setup.data} />
        </div>
      )}
      {mode === "runs" && setup.data && <FirstPayrollDoneBanner tenantSlug={tenantSlug} setup={setup.data} />}
      {(mode === "runs" || mode === "error" || (mode === "guide" && setup.data?.has_runs)) && <RunList />}
    </PayrollModuleShell>
  );
}
